export const BASE_URL = import.meta.env.VITE_API_BASE_URL as string;
console.log('[API] BASE_URL:=============>', BASE_URL);

// Set by main.ts after the router is ready — avoids a circular import.
let _navigateToLogin: (() => void) | null = null;
export function setNavigateToLogin(fn: () => void) {
  _navigateToLogin = fn;
}

// Called after every successful silent refresh so callers (e.g. biometric store) can
// keep their own copy of the refresh token in sync with the rotated value.
let _onRefreshSuccess: ((newRefreshToken: string) => void) | null = null;
export function setOnRefreshSuccess(fn: (newRefreshToken: string) => void) {
  _onRefreshSuccess = fn;
}

function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

function getCurrentLocationId(): number | null {
  try {
    const raw = localStorage.getItem('auth_user');
    if (!raw) return null;
    const user = JSON.parse(raw) as { location_id?: string | number };
    return user.location_id != null ? Number(user.location_id) : null;
  } catch {
    return null;
  }
}

function buildHeaders(contentType: 'json' | 'multipart' | 'none' = 'json'): HeadersInit {
  const headers: Record<string, string> = {};

  if (contentType === 'json') {
    headers['Content-Type'] = 'application/json';
  }

  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const locationId = getCurrentLocationId();
  if (locationId != null) headers['Location'] = String(locationId);

  return headers;
}

// ── Token refresh ────────────────────────────────────────────────────────────

// Single in-flight refresh — all concurrent 401s wait on the same promise.
let refreshingPromise: Promise<boolean> | null = null;

async function doSilentRefresh(): Promise<boolean> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${BASE_URL}/v2/users/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const json = await res.json().catch(() => ({})) as { status?: string; result?: { token?: string; refresh_token?: string } };
    if (res.ok && json.status === 'OK' && json.result?.token) {
      const newRefresh = json.result.refresh_token ?? '';
      localStorage.setItem('auth_token', json.result.token);
      localStorage.setItem('refresh_token', newRefresh);
      // Keep biometric Preferences in sync with the rotated refresh token
      _onRefreshSuccess?.(newRefresh);
      return true;
    }
  } catch { /* network failure */ }
  return false;
}

function clearSessionAndRedirect() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('session_expiry');
  localStorage.removeItem('auth_user');
  if (_navigateToLogin) {
    _navigateToLogin();
  } else {
    window.location.replace('/login');
  }
}

// ── Core request ─────────────────────────────────────────────────────────────

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isRetry = false,
): Promise<T> {
  const isFormData = body instanceof FormData;

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: buildHeaders(body === undefined ? 'none' : isFormData ? 'multipart' : 'json'),
    body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });

  // ── 401 handling ──────────────────────────────────────────────────────────
  if (res.status === 401 && !isRetry && !path.includes('/v2/users/refresh')) {
    // Coalesce concurrent 401s — only one refresh runs at a time
    if (!refreshingPromise) {
      refreshingPromise = doSilentRefresh().finally(() => { refreshingPromise = null; });
    }
    const refreshed = await refreshingPromise;

    if (refreshed) {
      return request<T>(method, path, body, true); // retry once with new token
    }

    clearSessionAndRedirect();
    throw new Error('Session expired. Please log in again.');
  }

  const data = await res.json().catch(() => ({}) as never);

  if (!res.ok) {
    throw new Error((data as { message?: string }).message ?? `Request failed (${res.status})`);
  }

  return data as T;
}

export const http = {
  get:    <T>(path: string)                 => request<T>('GET',    path),
  post:   <T>(path: string, body: unknown)  => request<T>('POST',   path, body),
  put:    <T>(path: string, body: unknown)  => request<T>('PUT',    path, body),
  patch:  <T>(path: string, body: unknown)  => request<T>('PATCH',  path, body),
  delete: <T>(path: string)                 => request<T>('DELETE', path),
  upload: <T>(path: string, form: FormData) => request<T>('POST',   path, form),
};
